/*
 * ApplySchemaTemplateModal — kippt einen bereits publizierten Schema-Baum
 * auf ein Device. Server-seitig legt der Endpoint Nodes + Leaves in den
 * gewählten Device-Sub-Tree; overwrite=true löscht bestehende harten.
 *
 * Auswahl-Fluß:
 *   1. GET /api/schema-templates → clientseitig auf das Device-Protokoll
 *      gefiltert (case-insensitive).
 *   2. Zeile klicken → selektieren.
 *   3. Overwrite-Checkbox — nur sichtbar wenn der Ziel-Baum NICHT leer ist,
 *      inklusive InfluxDB-Warnhinweis.
 *   4. Anwenden → POST apply-schema-template.
 *   5. 409 (TargetNotEmpty) → inline-Prompt „Overwrite aktivieren?".
 *   6. 409 (OrphanedBindings) → zweiter Haken, mit Zahlen. Der Server haelt
 *      an, wenn Dashboards, Auswertungen, HMI-Bilder oder Alarme an
 *      Datenpunkten haengen, die beim Anwenden verschwinden.
 *
 * DIE BEIDEN 409 SIND NICHT DASSELBE, und sie zu verwechseln macht den Riegel
 * zur Sackgasse. Bis zum 23.09.2026 behandelte dieses Modal jeden 409 als
 * „Baum nicht leer" und setzte den Overwrite-Haken zurueck. Der neue 409 waere
 * dort angekommen, haette die richtige Meldung gezeigt und keinen Weg
 * gelassen, sie zu bestaetigen. Unterschieden wird am Feld `orphans` im
 * Rumpf, nicht am Text: Texte werden umformuliert, Felder nicht.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { api, ApiError } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { IconCancel, TemplateApplying } from '../icons/HvacIcons';
// Die Kompatibilitäts-Familien lagen hier als zweite Kopie. Sie stehen
// jetzt einmal in api/protocols.ts — zusammen mit dem Gate, das den
// Discovery-Knopf auf Modbus-Geräten ersetzt (Konzept J.17). Eine
// Protokoll-Liste, ein Ort.
import { protocolsCompatible } from '../api/protocols';
import './hv-modal.css';

/** Was der Server zaehlt, wenn er wegen Bindungen anhaelt. */
interface OrphanUsage {
  kind: string;
  objects: number;
  bindings: number;
}

interface OrphanReport {
  leavesDisappearing: number;
  usages: OrphanUsage[];
}

export interface ApplyResult {
  nodesCreated: number;
  leavesCreated: number;
  nodesDeleted: number;
  leavesDeleted: number;
  credentialCopied: boolean;
}

interface TemplateListItem {
  id: string;
  name: string;
  protocol: string;
  version: number;
  scope: 0 | 1;
  updatedAt: string;
  /** Beschreibung ist im List-Response nicht garantiert — best-effort. */
  description?: string | null;
}

interface Props {
  plantId: string;
  deviceId: string;
  deviceProtocol: string | null;
  /** True → Overwrite-Checkbox bleibt versteckt, Apply läuft ohne Löschen.
   *  Bei false zwingen wir die Bestätigung. */
  isTargetEmpty: boolean;
  onClose(): void;
  onApplied(result: ApplyResult): void;
}

export function ApplySchemaTemplateModal({
  plantId,
  deviceId,
  deviceProtocol,
  isTargetEmpty,
  onClose,
  onApplied,
}: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [overwrite, setOverwrite] = useState(false);
  // Erst gesetzt, wenn der Server tatsaechlich angehalten hat. Der Haken
  // erscheint also nie auf Verdacht: wer eine Vorlage anwendet, an der nichts
  // haengt, sieht ihn gar nicht.
  const [orphans, setOrphans] = useState<OrphanReport | null>(null);
  const [acceptOrphans, setAcceptOrphans] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const listState = useAsync(
    (signal) => api.get<TemplateListItem[]>('/api/schema-templates', signal),
    [],
  );

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !applying) onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose, applying]);

  const filtered = useMemo(() => {
    if (listState.status !== 'ok') return [] as TemplateListItem[];
    const proto = (deviceProtocol ?? '').toLowerCase();
    return listState.data.filter((t) => protocolsCompatible(t.protocol, proto));
  }, [listState, deviceProtocol]);

  const selected = filtered.find((t) => t.id === selectedId) ?? null;

  const showOverwriteRow = !isTargetEmpty;
  const applyEnabled = selected !== null
    && !applying
    && (isTargetEmpty || overwrite)
    && (orphans === null || acceptOrphans);

  const apply = async () => {
    if (!selected) return;
    setApplying(true);
    setError(null);
    try {
      const res = await api.post<ApplyResult>(
        `/api/plants/${plantId}/devices/${deviceId}/apply-schema-template`,
        {
          templateId: selected.id,
          overwrite: showOverwriteRow ? overwrite : false,
          // Nur mitschicken, wenn der Server schon einmal angehalten hat und
          // der Bediener danach zugestimmt hat. Vorsorglich true zu senden
          // hiesse, den Riegel im Client abzuschalten.
          acceptOrphanedBindings: orphans !== null && acceptOrphans,
        },
      );
      onApplied(res);
      onClose();
    } catch (e) {
      if (e instanceof ApiError) {
        const bodyMsg = (e.body as { error?: string; detail?: string })?.error
          ?? (e.body as { detail?: string })?.detail;
        const report = (e.body as { orphans?: OrphanReport })?.orphans ?? null;
        if (e.status === 409 && report) {
          // Der Server hat wegen Bindungen angehalten. Der Overwrite-Haken
          // bleibt stehen, denn der war richtig; was fehlt, ist die zweite
          // Zustimmung. Kein Auto-Retry: das ist ja gerade der Punkt.
          setOrphans(report);
          setAcceptOrphans(false);
          setError(bodyMsg ?? 'Es hängen Bindungen an Datenpunkten, die dabei verschwinden.');
        } else if (e.status === 409) {
          // Server sagt Ziel-Baum ist doch nicht leer — Overwrite-Row
          // aufmachen und den Operator drum bitten, es explizit zu
          // bestätigen. Kein Auto-Retry hier: der harte Delete ist zu
          // teuer, um ihn ohne Klick durchzuwinken.
          setOverwrite(false);
          setError(bodyMsg
            ?? 'Baum ist nicht leer — Overwrite aktivieren, um bestehende Nodes/Leaves zu löschen.');
        } else if (e.status === 403) {
          setError(bodyMsg ?? 'Keine Berechtigung, Templates anzuwenden.');
        } else if (e.status === 404) {
          setError(bodyMsg ?? 'Template, Device oder Anlage nicht gefunden.');
        } else if (e.status === 400) {
          setError(bodyMsg ?? 'Ungültige Eingabe.');
        } else {
          setError(bodyMsg ?? `Anwenden fehlgeschlagen (${e.status}).`);
        }
      } else if (e instanceof Error) {
        setError(e.message);
      } else {
        setError('Unbekannter Fehler.');
      }
      setApplying(false);
    }
  };

  return createPortal(
    <div class="hv-modal-backdrop" onClick={() => !applying && onClose()}>
      <div class="hv-modal hv-modal--apply-template" onClick={(e) => e.stopPropagation()}>
        <div class="hv-modal__header">
          <h2 class="hv-modal__title">Schema-Template anwenden</h2>
          <button
            type="button"
            class="hv-modal__close"
            onClick={onClose}
            aria-label="Schließen"
            disabled={applying}
          >×</button>
        </div>
        <div class="hv-modal__body">
          <div class="hv-modal__hero" style={{ color: 'var(--accent)' }}>
            <TemplateApplying size={72} />
          </div>
          <p class="hv-modal__hint">
            Verfügbare Vorlagen für Protokoll
            {' '}<code>{deviceProtocol ?? '(unbekannt)'}</code>.
          </p>
          {listState.status === 'loading' && (
            <p class="hv-modal__hint">Lade Templates…</p>
          )}
          {listState.status === 'error' && (
            <p class="hv-modal__error">Templates konnten nicht geladen werden.</p>
          )}
          {listState.status === 'ok' && filtered.length === 0 && (
            <p class="hv-modal__hint">
              Für dieses Protokoll gibt es noch keine Templates. Auf der Anlagen-Seite über
              „Als Template speichern" auf einem eingerichteten Device eine anlegen.
            </p>
          )}
          {listState.status === 'ok' && filtered.length > 0 && (
            <div
              role="listbox"
              aria-label="Schema-Templates"
              class="hv-modal__listbox"
            >
              {filtered.map((t) => {
                const isSel = t.id === selectedId;
                return (
                  <div
                    key={t.id}
                    role="option"
                    aria-selected={isSel}
                    tabIndex={0}
                    class={`hv-modal__listbox-item${isSel ? ' hv-modal__listbox-item--active' : ''}`}
                    onClick={() => !applying && setSelectedId(t.id)}
                    onKeyDown={(e) => {
                      if ((e.key === 'Enter' || e.key === ' ') && !applying) {
                        e.preventDefault();
                        setSelectedId(t.id);
                      }
                    }}
                  >
                    <div class="hv-modal__listbox-item-title">
                      <span class="hv-modal__listbox-item-name">{t.name}</span>
                      <span class="hv-modal__listbox-item-badge">v{t.version}</span>
                      {t.scope === 1 && (
                        <span class="hv-modal__listbox-item-badge" title="Global sichtbar für alle Mandanten">
                          global
                        </span>
                      )}
                    </div>
                    {t.description && (
                      <div class="hv-modal__listbox-item-desc">{t.description}</div>
                    )}
                    <div class="hv-modal__listbox-item-meta">
                      Aktualisiert: {formatDate(t.updatedAt)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          {showOverwriteRow && selected && (
            <div class="hv-modal__field">
              <label>Overwrite</label>
              <label class={`hv-modal__checkbox-row hv-modal__checkbox-row--warn${applying ? ' hv-modal__checkbox-row--disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={overwrite}
                  onChange={(e) => setOverwrite((e.currentTarget as HTMLInputElement).checked)}
                  disabled={applying}
                />
                <span class="hv-modal__checkbox-row-label">
                  <strong>Bestehende Nodes und Leaves</strong> werden hart gelöscht und durch das
                  Template ersetzt.
                  <span class="hv-modal__checkbox-row-note">
                    History der aktuellen Leaves bleibt in InfluxDB, ist danach aber nicht mehr
                    im UI sichtbar. Kein Undo.
                  </span>
                </span>
              </label>
            </div>
          )}
          {orphans && (
            <div class="hv-modal__field">
              <label>Bindungen</label>
              <label class={`hv-modal__checkbox-row hv-modal__checkbox-row--warn${applying ? ' hv-modal__checkbox-row--disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={acceptOrphans}
                  onChange={(e) => setAcceptOrphans((e.currentTarget as HTMLInputElement).checked)}
                  disabled={applying}
                />
                <span class="hv-modal__checkbox-row-label">
                  <strong>{orphans.leavesDisappearing} Datenpunkte verschwinden</strong>, und daran
                  hängen:
                  <span class="hv-modal__checkbox-row-note">
                    {orphans.usages.map((u) => (
                      <span key={u.kind} style={{ display: 'block' }}>
                        {u.bindings} {u.bindings === 1 ? 'Bindung' : 'Bindungen'} in {u.objects}{' '}
                        {u.kind}
                      </span>
                    ))}
                    Diese zeigen danach ins Leere. Sie lassen sich nicht automatisch
                    wiederherstellen, die betroffenen Ansichten sind von Hand nachzubauen.
                  </span>
                </span>
              </label>
            </div>
          )}
          {error && <p class="hv-modal__error">{error}</p>}
        </div>
        <div class="hv-modal__footer">
          <div class="hv-modal__footer-spacer" />
          <button type="button" class="hv-btn" onClick={onClose} disabled={applying}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><IconCancel size={14} />Abbrechen</span>
          </button>
          <button
            type="button"
            class="hv-btn hv-btn--primary"
            onClick={apply}
            disabled={!applyEnabled}
            title={
              selected === null
                ? 'Zuerst ein Template wählen'
                : (showOverwriteRow && !overwrite
                    ? 'Overwrite bestätigen'
                    : (orphans !== null && !acceptOrphans
                        ? 'Verlust der Bindungen bestätigen'
                        : undefined))
            }
          >
            {applying ? 'Wende an…' : 'Anwenden'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString();
  } catch {
    return iso;
  }
}
